import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { Repeat2, Edit2, Trash2 } from 'lucide-react';
import ExecutionStatusOverlay from './ExecutionStatusOverlay';
import { getExecutionBorderClass, type ExecutionStatus } from './executionStatus';

interface TransformStepNodeData {
  stepName: string;
  transform?: any;
  executionStatus?: ExecutionStatus;
  onEdit?: () => void;
  onDelete?: () => void;
}

function TransformStepNode({ data, selected }: NodeProps<TransformStepNodeData>) {
  const keys = data.transform && typeof data.transform === 'object' ? Object.keys(data.transform).slice(0, 4) : [];

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[280px] max-w-[320px] relative ${
        selected ? 'border-teal-500 shadow-teal-200' : 'border-teal-300'
      } ${getExecutionBorderClass(data.executionStatus)}`}
    >
      <ExecutionStatusOverlay status={data.executionStatus} />
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-teal-500" />

      <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Repeat2 className="w-4 h-4" />
          <span className="font-semibold text-sm">Transform</span>
        </div>
        <div className="flex gap-1 nodrag nopan" style={{ pointerEvents: 'auto' }}>
          {data.onEdit && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-teal-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                data.onEdit?.();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Edit node"
            >
              <Edit2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
          {data.onDelete && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-red-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                data.onDelete?.();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Delete node"
            >
              <Trash2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
        </div>
      </div>

      <div className="p-3 space-y-2">
        <div className="font-semibold text-sm text-gray-900">{data.stepName}</div>

        {keys.length > 0 && (
          <div className="text-xs text-gray-600">
            Keys: <span className="font-mono">{keys.join(', ')}</span>
            {data.transform && Object.keys(data.transform).length > keys.length ? '…' : ''}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <Badge variant="secondary" className="text-xs">
            local
          </Badge>
          <span className="text-xs text-gray-500">$0.000</span>
        </div>
      </div>

      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-teal-500" />
    </div>
  );
}

export default memo(TransformStepNode);

